"use client";

import useSWR, {useSWRConfig, type SWRConfiguration, type SWRResponse} from "swr";
import useSWRInfinite from "swr/infinite";
import {apiClient} from "@/lib/api-client";
import {ChatSDKError} from "@/lib/errors";
import type {ApiResponse} from "@/lib/api/api-responses";
import type {Vote, Feedback, Chat} from "@/lib/db/schema";
import type {FeatureFlagsData, MessageStatus} from "@/lib/types";
import {createRecaptchaActionId} from "@/lib/utils";
import {getConfig} from "@/hooks/use-config";

declare global {
    interface Window {
        grecaptcha: {
            enterprise: {
                ready: (callback: () => void) => void;
                execute: (siteKey: string, options: { action: string }) => Promise<string>;
            };
        };
    }
}

/**
 * Default SWR configuration for API endpoints
 * Disables automatic revalidation to minimize unnecessary API calls
 */
const DEFAULT_SWR_CONFIG: SWRConfiguration = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
};

/**
 * Generic fetcher for API endpoints that wraps apiClient with error handling
 * @param url - The API endpoint URL (may include query parameters)
 * @returns Promise resolving to the API response data
 */
const createApiFetcher = <T>() => {
    return (url: string): Promise<ApiResponse<T>> => {
        // Extract params from URL and pass to apiClient
        const [path, query] = url.split('?');
        const params = query ? Object.fromEntries(new URLSearchParams(query)) : undefined;
        return apiClient.fetchThrowAs(ChatSDKError)(path, params);
    };
};

/**
 * Hook to fetch data from an API endpoint using SWR
 *
 * This hook provides a consistent pattern for API calls across the application:
 * - Automatic ApiResponse wrapper for all responses
 * - Automatic error handling with ChatSDKError
 * - Consistent caching behavior
 * - TypeScript type safety for API responses
 * - Support for conditional fetching (pass null as key to skip)
 * - Customizable SWR configuration
 *
 * @template T - The type of data expected in the API response payload (will be wrapped in ApiResponse<T>)
 * @param key - The API endpoint URL, or null to skip fetching
 * @param config - Optional SWR configuration to override defaults
 * @returns SWR response with typed data wrapped in ApiResponse, error, loading state, and mutate function
 *
 * @example
 * // Basic usage - ApiResponse wrapper is automatic
 * const { data, error, isLoading } = useApiEndpoint<UserData>("/api/user");
 * // data is typed as ApiResponse<UserData> | undefined
 * const userName = data?.name;
 *
 * @example
 * // Conditional fetching with query parameters
 * const { data } = useApiEndpoint<{votes: Vote[]}>(
 *   chatId ? `/api/vote?chatId=${chatId}` : null
 * );
 * const votes = data?.votes;
 *
 * @example
 * // Custom SWR configuration
 * const { data, mutate } = useApiEndpoint<FeatureFlagsData>(
 *   "/api/feature-flags",
 *   { revalidateOnMount: true }
 * );
 * const flags = data?.featureFlags;
 */
export function useApiEndpoint<T>(
    key: string | null,
    config?: SWRConfiguration
): SWRResponse<ApiResponse<T>> {
    return useSWR<ApiResponse<T>>(
        key,
        createApiFetcher<T>(),
        {
            ...DEFAULT_SWR_CONFIG,
            ...config,
        }
    );
}

// ============================================================================
// Feature Flags
// ============================================================================

/**
 * Hook to fetch user roles and feature flags from the Auth0 Management API.
 *
 * This hook:
 * - Fetches user roles from Auth0 Management API
 * - Parses roles to identify RAPIDS-specific roles (ALPHA, BETA, etc.)
 * - Fetches feature flags based on the parsed roles
 * - Caches results to minimize API calls
 *
 * @returns User roles, feature flags, loading state, error state, and mutate function
 */
export function useFeatureFlags() {
    const {data, error, isLoading, mutate} = useApiEndpoint<FeatureFlagsData>(
        "/api/feature-flags"
    );

    return {
        userRole: data?.userRole,
        featureFlags: data?.featureFlags ?? {},
        isLoading,
        error,
        mutate,
    };
}

// ============================================================================
// Votes
// ============================================================================

/**
 * Hook to fetch votes for a chat
 * @param chatId - The chat ID to fetch votes for, or null to skip fetching
 * @returns Votes data, loading state, error, and mutate function
 */
export function useVotes(chatId: string | null) {
    const {data, error, isLoading, mutate} = useApiEndpoint<{ votes: Vote[] }>(
        chatId ? `/api/vote/?chatId=${chatId}` : null
    );

    return {
        votes: data?.votes ?? [],
        isLoading,
        error,
        mutate,
    };
}

/**
 * Hook to submit a vote (upvote or downvote)
 * @returns Function to submit a vote
 */
export function useSubmitVote() {
    const {mutate} = useSWRConfig();

    const submitVote = async (params: {
        chatId: string;
        messageId: string;
        type: "up" | "down";
    }) => {
        const response = await apiClient.fetch("/api/vote/", {
            method: "PATCH",
            body: JSON.stringify(params),
        });

        // Invalidate votes cache for this chat
        await mutate(`/api/vote/?chatId=${params.chatId}`);

        return response;
    };

    return {submitVote};
}

// ============================================================================
// Feedback
// ============================================================================

/**
 * Hook to fetch feedbacks for a chat
 * @param chatId - The chat ID to fetch feedbacks for, or null to skip fetching
 * @returns Feedbacks data, loading state, error, and mutate function
 */
export function useFeedbacks(chatId: string | null) {
    const {data, error, isLoading, mutate} = useApiEndpoint<{ feedbacks: Feedback[] }>(
        chatId ? `/api/feedback/?chatId=${chatId}` : null
    );

    return {
        feedbacks: data?.feedbacks ?? [],
        isLoading,
        error,
        mutate,
    };
}

/**
 * Hook to submit feedback
 * @returns Function to submit feedback
 */
export function useSubmitFeedback() {
    const {mutate} = useSWRConfig();

    const submitFeedback = async (params: {
        chatId: string;
        messageId: string;
        comment: string;
    }) => {
        const response = await apiClient.fetch("/api/feedback/", {
            method: "POST",
            body: JSON.stringify(params),
        });

        // Invalidate feedbacks cache for this chat
        await mutate(`/api/feedback/?chatId=${params.chatId}`);

        return response;
    };

    return {submitFeedback};
}

// ============================================================================
// Sessions
// ============================================================================

export interface Session {
    sessionId: string;
    createdAt: string;
    deviceModel: string;
    browserName: string;
}

/**
 * Hook to fetch user sessions
 * @returns Sessions data, loading state, error, and mutate function
 */
export function useSessions() {
    const {data, error, isLoading, mutate} = useApiEndpoint<{ sessions: Session[] }>(
        "/api/auth/sessions"
    );

    return {
        sessions: data?.sessions ?? [],
        isLoading,
        error,
        mutate,
    };
}

/**
 * Hook to sign out of all sessions
 * @returns Function to sign out of all sessions
 */
export function useSignOutAllSessions() {
    const deleteAllSessions = async () => {
        return await apiClient.fetch("/api/auth/sessions", {
            method: "DELETE",
        });
    };

    return {deleteAllSessions};
}

// ============================================================================
// Chat History
// ============================================================================

export interface ChatHistory {
    chats: Array<Chat & {
        lastMessageStatus?: MessageStatus | null;
        lastMessageCreatedAt?: Date | null;
    }>;
    nextCursor: string | null;
}

const CHATS_PER_PAGE = 20;

/**
 * Get pagination key for chat history
 */
export const getChatHistoryPaginationKey = (
    pageIndex: number,
    previousPageData: ChatHistory | null
): [string, Record<string, any>] | null => {
    // No more pages
    if (previousPageData && !previousPageData.nextCursor) {
        return null;
    }

    // First page
    if (pageIndex === 0) {
        return [
            "/api/history/",
            {
                limit: CHATS_PER_PAGE,
            },
        ];
    }

    // Next pages
    return [
        "/api/history/",
        {
            limit: CHATS_PER_PAGE,
            cursor: previousPageData?.nextCursor,
        },
    ];
};

/**
 * Hook to fetch paginated chat history
 * @param enabled - Whether to enable fetching (e.g., only if user is authenticated)
 * @returns Paginated chat history data, loading state, error, and pagination functions
 */
export function useChatHistory(enabled: boolean = true) {
    const SIDEBAR_REFRESH_POLLING_MS = 30_000;

    const {
        data: paginatedChatHistories,
        error,
        size,
        setSize,
        isLoading,
        mutate,
    } = useSWRInfinite<ChatHistory>(
        enabled ? getChatHistoryPaginationKey : () => null,
        ([path, params]) => apiClient.fetchThrowAs(ChatSDKError)(path, params as Record<string, any>),
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            refreshInterval: SIDEBAR_REFRESH_POLLING_MS, // poll every 30 seconds to keep sidebar updated with new chats
        }
    );

    const hasMore = paginatedChatHistories
        ? paginatedChatHistories[paginatedChatHistories.length - 1]?.nextCursor !== null
        : false;

    const hasEmptyChatHistory = paginatedChatHistories
        ? paginatedChatHistories.every((page) => page.chats.length === 0)
        : false;

    const loadMore = () => {
        if (hasMore && !isLoading) {
            setSize(size + 1);
        }
    };

    return {
        paginatedChatHistories,
        isLoading,
        error,
        hasMore,
        hasEmptyChatHistory,
        loadMore,
        mutate,
    };
}

// ============================================================================
// Labels
// ============================================================================

export interface LabelsData {
    labels: Record<string, string>;
    locale: string;
}

/**
 * Hook to fetch labels from Contentful
 *
 * Features:
 * - Server-side SWR caching (2h fresh, 24h max stale)
 * - Client-side polling for live updates (default: 5 minutes)
 * - Enables dynamic updates without page refresh (e.g., WARNING_MESSAGE_BANNER)
 *
 * @param refreshIntervalMs - Client-side polling interval (0 = disabled, default: 5 minutes)
 * @returns Labels data, loading state, error, and mutate function
 */
export function useLabels(refreshIntervalMs: number = 5 * 60 * 1000) {
    const {data, error, isLoading, mutate} = useApiEndpoint<LabelsData>(
        "/api/labels/",
        {
            refreshInterval: refreshIntervalMs,
            revalidateOnMount: true, // Always fetch latest on mount
        }
    );

    return {
        labels: data?.labels,
        locale: data?.locale,
        isLoading,
        error,
        mutate,
    };
}

// ============================================================================
// Sample Questions
// ============================================================================

export interface SampleQuestionsData {
    sampleQuestions: string[];
    locale: string;
}

/**
 * Hook to fetch sample questions from Contentful
 *
 * Features:
 * - Server-side SWR caching (2h fresh, 24h max stale)
 * - Client-side polling for live updates (default: 5 minutes)
 * - Feature flag filtering on the server
 *
 * @param refreshIntervalMs - Client-side polling interval (0 = disabled, default: 5 minutes)
 * @returns Sample questions, loading state, error, and mutate function
 */
export function useSampleQuestions(refreshIntervalMs: number = 5 * 60 * 1000) {
    const {data, error, isLoading, mutate} = useApiEndpoint<SampleQuestionsData>(
        "/api/sample-questions",
        {
            refreshInterval: refreshIntervalMs,
            revalidateOnMount: true, // Always fetch latest on mount
        }
    );

    return {
        sampleQuestions: data?.sampleQuestions ?? [],
        locale: data?.locale,
        isLoading,
        error,
        mutate,
    };
}

// ============================================================================
// Support Tickets
// ============================================================================

/**
 * Execute reCAPTCHA and get token
 * @param siteKey - reCAPTCHA site key
 * @param rapidsVerticalName - RAPIDS vertical name for action ID generation
 * @returns Promise resolving to the reCAPTCHA token
 */
async function executeRecaptcha(siteKey: string, rapidsVerticalName: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        if (!window.grecaptcha?.enterprise) {
            reject(new Error("reCAPTCHA not loaded"));
            return;
        }

        // Generate reCAPTCHA action ID
        const recaptchaActionId = createRecaptchaActionId(rapidsVerticalName, 'submit_support_ticket');

        window.grecaptcha.enterprise.ready(async () => {
            try {
                const token = await window.grecaptcha.enterprise.execute(
                    siteKey,
                    {action: recaptchaActionId}
                );
                resolve(token);
            } catch (err) {
                reject(err);
            }
        });
    });
}

/**
 * Hook to cancel an A2A chat task
 *
 * Supports two modes:
 * 1. With taskId: Cancel the A2A task immediately
 * 2. Without taskId: Request backend to cancel when task ID becomes available
 *
 * Number 2 is needed to support cancelling chat streams before we have access to the task ID.
 * The logical solution would be to use AbortSignals, but unfortunately they are not compatible with resumable streams yet...
 * Vercel is working on a solution for this, and when it is implemented we should migrate and simplify this.
 * https://ai-sdk.dev/docs/advanced/stopping-streams
 * https://github.com/vercel/ai/issues/8390
 *
 * @returns Function to cancel a task by sending a cancel-task RPC to the backend
 */
export function useCancelTask() {
    const cancelTask = async (params: {
        chatId: string;
        taskId?: string;
    }) => {
        return await apiClient.fetch(`/api/chat/${params.chatId}/cancel/`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({
                taskId: params.taskId,
            }),
        });
    };

    return {cancelTask};
}

/**
 * Hook to submit a support ticket
 * @returns Function to submit a support ticket with automatic reCAPTCHA handling
 */
export function useSubmitSupportTicket() {
    const submitSupportTicket = async (params: {
        email: string;
        subject: string;
        description: string;
        category: string;
    }) => {
        const {recaptchaSiteId, rapidsVerticalName} = await getConfig();

        // Execute reCAPTCHA to get token
        const captchaToken = await executeRecaptcha(recaptchaSiteId, rapidsVerticalName);

        // Submit with captcha token
        const response = await apiClient.fetch("/api/support", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...params,
                captchaToken,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Failed to submit support ticket");
        }

        return response.json();
    };

    return {submitSupportTicket};
}